import { formatMoney } from '../../Common/Helpers/FormatMoney';
import { sfUrl } from '../StorefrontPaths';
import { sizedImage } from '../StorefrontImages';

/**
 * Search-as-you-type for the storefront header search box. Shows the first parts the full
 * search would return (same filters, active vehicle included) and matching categories, with a
 * "search all" row at the bottom. Typing pauses briefly before asking, a newer keystroke aborts
 * the older request, and answers are remembered for the page's lifetime.
 * Keyboard: ↑/↓ move, Enter opens the highlighted row (or searches), Esc closes.
 */

interface SuggestPart {
    Id: number;
    Title: string;
    Slug: string;
    ImageUrl: string;
    Sku: string;
    OemNumber: string;
    Price: number | null;
}

interface SuggestCategory {
    CategoryId: number;
    SubCategoryId: number | null;
    Name: string;
    ParentName: string | null;
}

interface SuggestResponse {
    Parts: SuggestPart[];
    Categories: SuggestCategory[];
}

const MIN_LENGTH = 2;       // mirrors SearchSuggest.MinLength on the server
const DEBOUNCE_MS = 200;

const esc = (s: unknown) => String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);

/** Codes not already spelled out in the title (CarParts titles usually include "SKU … OEM …"). */
function extraCodes(p: SuggestPart): string {
    const title = (p.Title ?? '').toLowerCase();
    const missing = (code: string) => !!code && !title.includes(code.toLowerCase());
    return [missing(p.Sku) ? p.Sku : '', missing(p.OemNumber) ? `OEM ${p.OemNumber}` : ''].filter(Boolean).join(' · ');
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Escapes text and bolds the parts that match any typed word. */
function highlight(text: string, term: string): string {
    const words = term.split(/\s+/).filter(w => w.length > 0).map(escapeRegExp);
    if (!text || words.length === 0)
        return esc(text);
    const re = new RegExp(`(${words.join('|')})`, 'gi');
    return text.split(re).map((chunk, i) => i % 2 === 1 ? `<mark>${esc(chunk)}</mark>` : esc(chunk)).join('');
}

class SearchSuggestBox {
    private readonly form: HTMLFormElement;
    private readonly input: HTMLInputElement;
    private readonly panel: HTMLDivElement;
    private readonly cache = new Map<string, SuggestResponse>();
    private timer: number | undefined;
    private inflight: AbortController | null = null;
    private term = '';
    private active = -1;

    constructor(form: HTMLFormElement, input: HTMLInputElement) {
        this.form = form;
        this.input = input;

        this.panel = document.createElement('div');
        this.panel.className = 'sf-suggest';
        this.panel.id = 'sf-suggest';
        this.panel.setAttribute('role', 'listbox');
        this.panel.hidden = true;
        (input.closest('.sf-header-search-field') ?? form).appendChild(this.panel);

        input.setAttribute('autocomplete', 'off');
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-autocomplete', 'list');
        input.setAttribute('aria-controls', this.panel.id);
        input.setAttribute('aria-expanded', 'false');

        input.addEventListener('input', () => this.schedule());
        input.addEventListener('focus', () => { if (this.panel.innerHTML && this.term === input.value.trim()) this.open(); });
        input.addEventListener('keydown', e => this.onKey(e));
        // Clicking a row navigates; keep the input focused until then so blur doesn't close first.
        this.panel.addEventListener('mousedown', e => e.preventDefault());
        document.addEventListener('click', e => {
            if (!form.contains(e.target as Node))
                this.close();
        });
    }

    private schedule(): void {
        window.clearTimeout(this.timer);
        const term = this.input.value.trim();
        if (term.length < MIN_LENGTH) {
            this.inflight?.abort();
            this.term = '';
            this.close();
            return;
        }
        this.timer = window.setTimeout(() => this.fetch(term), DEBOUNCE_MS);
    }

    private async fetch(term: string): Promise<void> {
        const key = term.toLowerCase();
        const cached = this.cache.get(key);
        if (cached) {
            this.render(term, cached);
            return;
        }

        this.inflight?.abort();
        const controller = new AbortController();
        this.inflight = controller;
        try {
            const res = await fetch('/Services/Storefront/Catalog/Suggest', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Search: term }),
                signal: controller.signal
            });
            if (!res.ok)
                return;
            const data = await res.json() as SuggestResponse;
            this.cache.set(key, data);
            // Only show it if it's still what's in the box.
            if (this.input.value.trim() === term)
                this.render(term, data);
        } catch {
            // Aborted by a newer keystroke, or offline — suggestions are optional.
        } finally {
            if (this.inflight === controller)
                this.inflight = null;
        }
    }

    private render(term: string, data: SuggestResponse): void {
        this.term = term;
        this.active = -1;
        const parts = data.Parts ?? [];
        const categories = data.Categories ?? [];

        const partRows = parts.map(p => {
            const href = p.Slug
                ? sfUrl('/product/' + encodeURIComponent(p.Slug))
                : sfUrl('/catalog?search=' + encodeURIComponent(p.Sku || p.Title || term));
            const codes = extraCodes(p);
            return `<a class="sf-suggest-row sf-suggest-part" role="option" href="${esc(href)}">
                    <span class="sf-suggest-thumb">${p.ImageUrl ? `<img src="${esc(sizedImage(p.ImageUrl, 'thumb'))}" alt="" />` : '<i class="fa fa-cog"></i>'}</span>
                    <span class="sf-suggest-text">
                        <span class="sf-suggest-title">${highlight(p.Title, term)}</span>
                        ${codes ? `<span class="sf-suggest-meta">${highlight(codes, term)}</span>` : ''}
                    </span>
                    ${p.Price != null ? `<span class="sf-suggest-price">${esc(formatMoney(p.Price))}</span>` : ''}
                </a>`;
        }).join('');

        const categoryRows = categories.map(c => {
            const query = c.SubCategoryId
                ? `categoryId=${c.CategoryId}&subCategoryId=${c.SubCategoryId}`
                : `categoryId=${c.CategoryId}`;
            return `<a class="sf-suggest-row sf-suggest-category" role="option" href="${esc(sfUrl('/catalog?' + query))}">
                    <i class="fa fa-folder-open-o" aria-hidden="true"></i>
                    <span class="sf-suggest-text">${highlight(c.Name, term)}${c.ParentName ? ` <span class="sf-suggest-meta">in ${esc(c.ParentName)}</span>` : ''}</span>
                </a>`;
        }).join('');

        const searchAll = `<a class="sf-suggest-row sf-suggest-all" role="option" href="${esc(sfUrl('/catalog?search=' + encodeURIComponent(term)))}">
                <i class="fa fa-search" aria-hidden="true"></i>
                <span class="sf-suggest-text">${parts.length || categories.length ? 'See all results for' : 'No quick matches — search for'} <strong>“${esc(term)}”</strong></span>
            </a>`;

        this.panel.innerHTML =
            (partRows ? `<div class="sf-suggest-section"><div class="sf-suggest-heading">Parts</div>${partRows}</div>` : '') +
            (categoryRows ? `<div class="sf-suggest-section"><div class="sf-suggest-heading">Categories</div>${categoryRows}</div>` : '') +
            searchAll;
        this.open();
    }

    private rows(): HTMLAnchorElement[] {
        return Array.from(this.panel.querySelectorAll<HTMLAnchorElement>('.sf-suggest-row'));
    }

    private onKey(e: KeyboardEvent): void {
        if (this.panel.hidden)
            return;
        const rows = this.rows();
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const step = e.key === 'ArrowDown' ? 1 : -1;
            // Cycle through -1 (the text box), 0 … rows.length - 1.
            this.setActive((this.active + 1 + step + rows.length + 1) % (rows.length + 1) - 1, rows);
        } else if (e.key === 'Enter' && this.active >= 0 && rows[this.active]) {
            e.preventDefault();
            window.location.href = rows[this.active].href;
        } else if (e.key === 'Escape') {
            this.close();
        }
    }

    /** -1 means "back in the text box". */
    private setActive(index: number, rows: HTMLAnchorElement[]): void {
        rows.forEach((r, i) => {
            r.classList.toggle('is-active', i === index);
            r.id = `sf-suggest-row-${i}`;
        });
        this.active = index;
        if (index >= 0) {
            this.input.setAttribute('aria-activedescendant', rows[index].id);
            rows[index].scrollIntoView({ block: 'nearest' });
        } else {
            this.input.removeAttribute('aria-activedescendant');
        }
    }

    private open(): void {
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
    }

    private close(): void {
        this.panel.hidden = true;
        this.active = -1;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
    }
}

/** Attaches suggestions to the header search box, if the page has one. */
export function initSearchSuggest(): void {
    const form = document.querySelector<HTMLFormElement>('form.sf-header-search');
    const input = form?.querySelector<HTMLInputElement>('input[name="search"]');
    if (form && input)
        new SearchSuggestBox(form, input);
}
